import React from "react";
import styled, { FlattenSimpleInterpolation } from "styled-components";

const flDanger60 = "#ECD2D2";
const flDangerBorder = "#CF6B6B";
const flSuccess60 = "#CAE5C8";
const flSuccessBorder = "#8EB88A";
const flGeneral60 = "#D4E2F0";
const flGeneralBorder = "#7EA4C7";
const flWarning60 = "#ECE6C3";
const flWarningBorder = "#CCBF71";

/** notification card, e.g. used at the top of each page during a service outage */
export function NotificationBanner({
  message,
  icon,
  variant,
  customCss,
  ActionButton,
  ...rest
}: {
  variant: "error" | "warning" | "info" | "success";
  message: string | React.ReactElement;
  icon: React.ReactElement;
  customCss?: string | FlattenSimpleInterpolation;
  ActionButton?: React.ReactElement;
} & React.HTMLAttributes<HTMLDivElement>): React.JSX.Element {
  return (
    <NotificationBannerStyles
      $colors={
        variant === "error"
          ? {
              background: flDanger60,
              border: flDangerBorder,
              dropShadowOpacity: 0.15,
            }
          : variant === "success"
            ? { background: flSuccess60, border: flSuccessBorder }
            : variant === "info"
              ? { background: flGeneral60, border: flGeneralBorder }
              : variant === "warning"
                ? { background: flWarning60, border: flWarningBorder } // placeholder
                : { background: "#ffffff", border: "#000000" }
      }
      $customCss={customCss}
      {...rest}
    >
      <div className="icon">{icon}</div>
      <div className="message">{message}</div>
      {ActionButton}
    </NotificationBannerStyles>
  );
}
// convert hex to hex + opacity https://stackoverflow.com/a/66143374
const setOpacity = (hex: string, alpha: number) =>
  `${hex}${Math.floor(alpha * 255)
    .toString(16)
    .padStart(2, "0")}`;
const NotificationBannerStyles = styled.div<{
  $customCss?: string | FlattenSimpleInterpolation;
  $colors: {
    background: string;
    border: string;
    dropShadowOpacity?: number;
  };
}>`
  color: #000;
  z-index: 1;
  display: flex;
  height: 64px;
  font-size: 12px;
  box-sizing: border-box;
  border-radius: 10px;
  border: 2px solid ${({ $colors }) => $colors.border};
  padding: 12px 16px 12px 22px;
  background: ${({ $colors }) => $colors.background};
  box-shadow: 0px 4px 8px 0px
    ${({ $colors }) => setOpacity($colors.border, $colors.dropShadowOpacity ?? 0.25)};
  position: relative;
  justify-content: start;
  align-items: center;
  gap: 12px;
  .icon {
    margin-bottom: -2px;
  }
  .message {
    line-height: 18px;
    flex-grow: 1;
  }
  ${({ $customCss }) => $customCss || ``}
`;
